import { useEffect, useMemo, useRef, useState } from 'react';

import { Api } from '../../services/api';
import { normalizeProducts } from '../../services/productNormalizer';
import {
  clearSelectedProduct,
  getSelectedProduct,
} from '../../services/productSelection';
import ProductsSection from '../home/ProductsSection';
import { resolvePriceInfo, formatAmount } from '../../services/pricing';
import heartIcon from '../../assets/heart.png';
import heartFillIcon from '../../assets/heart-fill.png';
import { addItemToCart } from '../../services/cart';
import { useLanguage } from '../../context/LanguageContext';
import { getStoredCustomerId } from '../../services/customer';
import { toggleProductFavorite } from '../../services/favorites';
import { redirectToLogin } from '../../services/auth';
import {
  getReadableTextColor,
  getVariantColorKey,
  normalizeColorCode,
} from '../../services/colorUtils';

/* ================= CONSTANTS ================= */

const SIZE_ORDER = {
  xs: 1,
  s: 2,
  sm: 2,
  small: 2,
  m: 3,
  md: 3,
  medium: 3,
  l: 4,
  lg: 4,
  large: 4,
  xl: 5,
  xlarge: 5,
  xxl: 6,
};

const PRODUCT_COLOR_FALLBACK = '#d9d9d9';

/* ================= PAGE ================= */

const ProductDetailsPage = () => {
  const { t } = useLanguage();
  const customerId = useMemo(getStoredCustomerId, []);

  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState('idle');

  const [selectedImage, setSelectedImage] = useState('');
  const [selectedColorId, setSelectedColorId] = useState(null);
  const [selectedSizeId, setSelectedSizeId] = useState(null);

  const [favoritePending, setFavoritePending] = useState(false);
  const [addMessage, setAddMessage] = useState('');
  const addMessageTimer = useRef(null);

  /* ================= LOAD PRODUCT ================= */

  useEffect(() => {
    const cached = getSelectedProduct();
    if (cached) {
      setProduct(cached);
      setStatus('success');
      clearSelectedProduct();
      return;
    }

    const [, id] = window.location.pathname.split('/product/');
    if (!id) return;

    let cancelled = false;

    const load = async () => {
      setStatus('loading');
      try {
        const data = await Api.fetchProducts(customerId);
        if (cancelled) return;

        const normalized = normalizeProducts(data);
        const match = normalized.find(
          (p) => String(p.productId || p.id) === String(id)
        );

        if (match) {
          setProduct(match);
          setStatus('success');
        } else {
          setStatus('error');
        }
      } catch {
        setStatus('error');
      }
    };

    load();
    return () => (cancelled = true);
  }, [customerId]);

  const variants = product?.variants || [];

  /* ================= COLORS (DEDUPED) ================= */

  const colorOptions = useMemo(() => {
    const map = new Map();

    variants.forEach((v) => {
      const key = getVariantColorKey(v);
      if (!key) return;

      if (!map.has(key)) {
        map.set(key, {
          id: key,
          label: v.colorName || t('product.color_label'),
          swatch:
            normalizeColorCode(v.colorCode || v.color?.code) ||
            PRODUCT_COLOR_FALLBACK,
        });
      }
    });

    return Array.from(map.values());
  }, [variants, t]);

  /* ================= SIZES FOR COLOR ================= */

  const sizesForColor = useMemo(() => {
    if (!selectedColorId) return [];

    const map = new Map();

    variants.forEach((v) => {
      if (getVariantColorKey(v) !== selectedColorId) return;
      const key = v.sizeId || v.sizeName;
      if (!key) return;

      map.set(key, {
        id: key,
        label: v.sizeName || key,
      });
    });

    return Array.from(map.values()).sort((a, b) => {
      const ak = a.label.toLowerCase();
      const bk = b.label.toLowerCase();
      return (SIZE_ORDER[ak] ?? 999) - (SIZE_ORDER[bk] ?? 999);
    });
  }, [variants, selectedColorId]);

  /* ================= AUTO SELECT SIZE ================= */

  useEffect(() => {
    if (!sizesForColor.length) {
      setSelectedSizeId(null);
      return;
    }

    if (!selectedSizeId) {
      setSelectedSizeId(sizesForColor[0].id);
    }
  }, [sizesForColor, selectedSizeId]);

  /* ================= SELECTED VARIANT ================= */

  const selectedVariant = useMemo(() => {
    if (!selectedColorId) return null;

    const byColor = variants.filter(
      (v) => getVariantColorKey(v) === selectedColorId
    );

    const colorHasSizes = byColor.some((v) => v.sizeId || v.sizeName);

    if (!colorHasSizes) {
      return byColor[0] || null;
    }

    if (!selectedSizeId) return null;

    return (
      byColor.find(
        (v) =>
          v.sizeId === selectedSizeId ||
          v.sizeName === selectedSizeId
      ) || null
    );
  }, [variants, selectedColorId, selectedSizeId]);

  /* ================= INITIAL + DYNAMIC GALLERY ================= */

  const galleryImages = useMemo(() => {
    const images = [];
    const seen = new Set();
    const colorSeen = new Set();

    const pushImage = (img) => {
      if (img && !seen.has(img)) {
        seen.add(img);
        images.push(img);
      }
    };

    // 1️⃣ If exact variant selected → show only its images
    if (selectedVariant?.images?.length) {
      selectedVariant.images.forEach(pushImage);
      return images;
    }

    // 2️⃣ Color selected → show first variant of that color
    if (selectedColorId) {
      const first = variants.find(
        (v) => getVariantColorKey(v) === selectedColorId
      );
      if (first?.images?.length) {
        first.images.forEach(pushImage);
        return images;
      }
    }

    // 3️⃣ Initial load → product images
    (product?.images || []).forEach(pushImage);

    // 4️⃣ One variant per color (FIRST size only)
    variants.forEach((v) => {
      const colorKey = getVariantColorKey(v);
      if (!colorKey || colorSeen.has(colorKey)) return;

      colorSeen.add(colorKey);
      (v.images || []).forEach(pushImage);
    });

    return images.length ? images : [product?.image].filter(Boolean);
  }, [product, variants, selectedColorId, selectedVariant]);

  /* ================= SYNC SELECTED IMAGE ================= */

  useEffect(() => {
    if (galleryImages.length) {
      setSelectedImage(galleryImages[0]);
    }
  }, [galleryImages]);

  /* ================= PRICING ================= */

  const priceRow = resolvePriceInfo(product, selectedVariant);

  /* ================= ADD TO CART ================= */

  const handleAddToCart = () => {
    if (variants.length && !selectedVariant) {
      alert(t('product.select_options'));
      return;
    }

    const variant = selectedVariant;
    const basePrice =
      variant?.priceOverride ??
      priceRow.original ??
      product.price ??
      0;

    addItemToCart({
      productId: product.productId || product.id,
      variantId: variant?.variantId || 'default',
      name: product.name,
      image: selectedImage,
      quantity: 1,
      priceUnit: priceRow.current,
      originalPrice: basePrice,
      discount: priceRow.showOriginal,
      discountPercentage: priceRow.discountPercentage,
      colorName: variant?.colorName,
      sizeName: variant?.sizeName,
      shipping: Number(
        (variant && variant.shipping != null ? variant.shipping : product?.shipping) ||
          0
      ),
    });

    setAddMessage(t('product.added'));
    clearTimeout(addMessageTimer.current);
    addMessageTimer.current = setTimeout(() => setAddMessage(''), 2500);
  };

  /* ================= FAVORITE ================= */

  const handleFavoriteToggle = async () => {
    if (!customerId) {
      redirectToLogin();
      return;
    }
    if (favoritePending) return;

    setFavoritePending(true);
    try {
      const result = await toggleProductFavorite({ product, customerId });
      setProduct((p) => ({ ...p, ...result }));
    } finally {
      setFavoritePending(false);
    }
  };

  if (!product) {
    return <div className="product-details">{t('product.loading')}</div>;
  }

  const description = (product.description || '').trim();

  /* ================= RENDER ================= */

  return (
    <div className="product-details">
      <section className="product-details__hero">
        <div className="product-details__gallery">
          <div className="product-details__preview">
            <img src={selectedImage} alt={product.name} />
          </div>

          <div className="product-details__thumbs">
            {galleryImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setSelectedImage(img)}
                className={img === selectedImage ? 'is-active' : ''}
              >
                <img src={img} alt="" />
              </button>
            ))}
          </div>
        </div>

        <div className="product-details__summary">
          <h1>{product.name}</h1>

          <div className="product-details__price">
            {priceRow.showOriginal && (
              <span className="old">${formatAmount(priceRow.original)}</span>
            )}
            <span className="current">${formatAmount(priceRow.current)}</span>
          </div>

          {/* COLORS */}
          {colorOptions.length > 0 && (
            <div className="product-details__section">
              <p>{t('product.choose_color')}</p>
              <div className="product-details__swatches">
                {colorOptions.map((c) => (
                  <button
                    key={c.id}
                    className={`swatch ${
                      c.id === selectedColorId ? 'swatch--active' : ''
                    }`}
                    onClick={() => {
                      setSelectedColorId(c.id);
                      setSelectedSizeId(null);
                    }}
                  >
                    <span
                      className="swatch__fill"
                      style={{ backgroundColor: c.swatch }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* SIZES */}
          {sizesForColor.length > 0 && (
            <div className="product-details__section">
              <p>{t('product.choose_size')}</p>
              <div className="product-details__sizes">
                {sizesForColor.map((s) => (
                  <button
                    key={s.id}
                    className={`size-pill ${
                      s.id === selectedSizeId ? 'size-pill--active' : ''
                    }`}
                    onClick={() => setSelectedSizeId(s.id)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {description && (
            <div className="product-details__section">
              <p>{t('product.description_title')}</p>
              <p className="product-details__description">{description}</p>
            </div>
          )}

          <div className="product-details__actions">
            <button
              className="button product-details__cta"
              onClick={handleAddToCart}
              disabled={variants.length > 0 && !selectedVariant}
            >
              {t('product.add_to_cart')}
            </button>

            <button
              className="product-details__wishlist"
              onClick={handleFavoriteToggle}
            >
              <img
                src={product.favorite ? heartFillIcon : heartIcon}
                alt=""
              />
            </button>
          </div>

          {addMessage && <p className="product-details__note">{addMessage}</p>}
        </div>
      </section>

      <ProductsSection
        title={t('products.you_might_like')}
        variant="compact"
        limit={4}
      />
    </div>
  );
};

export default ProductDetailsPage;
